import { useState, useEffect, useCallback } from "react";
import { MobileLayout } from "../../components/MobileLayout";
import { useApp } from "../../context/AppContext";
import { supabase } from "../../lib/supabase";
import { toast } from "sonner";
import { Plus, X, Check, Clock, Megaphone, Eye, ChevronDown, ChevronUp, Archive } from "lucide-react";

const EMPRESA_ID = "ff438dc8-89fb-4d7c-9ea2-7e09162f242a";

export const NIVEL_CFG = {
  normal:     { label: "Normal",     bg: "#F0FDF4", border: "#BBF7D0", text: "#15803D", dot: "#22C55E" },
  importante: { label: "Importante", bg: "#FFFBEB", border: "#FDE68A", text: "#92400E", dot: "#F59E0B" },
  urgente:    { label: "Urgente",    bg: "#FEF2F2", border: "#FECACA", text: "#991B1B", dot: "#EF4444" },
};

const normNivel = (n) => {
  if (n === "info") return "normal";
  if (n === "atencao") return "importante";
  return NIVEL_CFG[n] ? n : "normal";
};

const fmtDT = (iso) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

const fmtDate = (d) =>
  d ? new Date(d + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" }) : null;

const expirado = (validade) => {
  if (!validade) return false;
  const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
  return new Date(validade + "T12:00:00") < hoje;
};

export default function Avisos() {
  const { user, marcarAvisoLido, avisosNaoLidos, recarregarAvisos } = useApp();
  const nomeUsuario = user?.usuarioNome || user?.nome || "Usuário";
  const userId      = String(user?.usuarioId || user?.id || "anon");

  const [avisos,       setAvisos]       = useState([]);
  const [leituras,     setLeituras]     = useState(new Set()); // IDs já lidos pelo usuário atual
  const [loading,      setLoading]      = useState(true);
  const [modalAberto,  setModalAberto]  = useState(false);
  const [editando,     setEditando]     = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [arquivExpand, setArquivExpand] = useState(false);
  const [form,         setForm]         = useState({ titulo: "", descricao: "", nivel: "normal", validade: "" });

  const carregar = useCallback(async () => {
    const [{ data: avData }, { data: leitData }] = await Promise.all([
      supabase
        .from("liberty_avisos")
        .select("*")
        .eq("empresa_id", EMPRESA_ID)
        .order("criado_em", { ascending: false }),
      supabase
        .from("liberty_avisos_leituras")
        .select("aviso_id")
        .eq("usuario_id", userId),
    ]);
    setAvisos(avData || []);
    setLeituras(new Set((leitData || []).map((l) => l.aviso_id)));
    setLoading(false);
  }, [userId]);

  useEffect(() => { carregar(); }, [carregar]);

  // Separa ativos e arquivados
  const ativos    = avisos.filter((a) => a.status === "ativo" && !expirado(a.validade));
  const arquivados = avisos.filter((a) => a.status === "arquivado" || expirado(a.validade));
  const naoLidosLocais = ativos.filter((a) => !leituras.has(a.id)).length;

  // ---------- ações ----------

  const abrirNovo = () => {
    setEditando(null);
    setForm({ titulo: "", descricao: "", nivel: "normal", validade: "" });
    setModalAberto(true);
  };

  const abrirEditar = (aviso) => {
    setEditando(aviso);
    setForm({ titulo: aviso.titulo, descricao: aviso.descricao || "", nivel: normNivel(aviso.nivel), validade: aviso.validade || "" });
    setModalAberto(true);
  };

  const fecharModal = () => { setModalAberto(false); setEditando(null); };

  const salvar = async () => {
    if (!form.titulo.trim()) return;
    setSaving(true);
    try {
      if (editando) {
        const hist = [...(editando.historico_edicoes || []), {
          editado_por: nomeUsuario, editado_em: new Date().toISOString(),
          titulo_anterior: editando.titulo, descricao_anterior: editando.descricao,
        }];
        await supabase.from("liberty_avisos").update({
          titulo: form.titulo.trim(), descricao: form.descricao.trim() || null,
          nivel: form.nivel, validade: form.validade || null, historico_edicoes: hist,
        }).eq("id", editando.id);
      } else {
        await supabase.from("liberty_avisos").insert({
          empresa_id: EMPRESA_ID, titulo: form.titulo.trim(),
          descricao: form.descricao.trim() || null, nivel: form.nivel,
          validade: form.validade || null, criado_por: nomeUsuario, status: "ativo",
        });
      }
      toast.success(editando ? "Aviso atualizado!" : "Aviso criado!");
      fecharModal();
      carregar();
      recarregarAvisos?.();
    } catch (err) {
      console.error("[Avisos]", err);
      toast.error("Erro ao salvar aviso. Tente novamente.");
    }
    setSaving(false);
  };

  const arquivar = async (aviso) => {
    await supabase.from("liberty_avisos").update({
      status: "arquivado", arquivado_por: nomeUsuario, arquivado_em: new Date().toISOString(),
    }).eq("id", aviso.id);
    carregar();
    recarregarAvisos?.();
  };

  const handleLido = async (aviso) => {
    if (leituras.has(aviso.id)) return;
    await supabase.from("liberty_avisos_leituras").upsert(
      { aviso_id: aviso.id, usuario_id: userId, usuario_nome: nomeUsuario },
      { onConflict: "aviso_id,usuario_id" }
    );
    setLeituras((s) => new Set([...s, aviso.id]));
    marcarAvisoLido?.();
  };

  return (
    <MobileLayout title="Avisos" scrollable>
      {/* Botão novo aviso */}
      <button
        type="button"
        onClick={abrirNovo}
        className="w-full flex items-center justify-center gap-2 py-3.5 mb-4 rounded-2xl font-bold text-[14px] text-white active:scale-[0.98] transition"
        style={{ background: "#08111F" }}
      >
        <Plus className="w-5 h-5" />
        Novo aviso
      </button>

      {/* Contador de não lidos */}
      {naoLidosLocais > 0 && (
        <div className="flex items-center gap-2 mb-3 px-1">
          <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 animate-pulse" />
          <p className="text-[12px] font-medium" style={{ color: "#3b82f6" }}>
            {naoLidosLocais} {naoLidosLocais === 1 ? "aviso não lido" : "avisos não lidos"}
          </p>
        </div>
      )}

      {/* Lista de avisos ativos */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 rounded-2xl animate-pulse" style={{ background: "#e8eaf0" }} />
          ))}
        </div>
      ) : ativos.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-14 text-center">
          <Megaphone className="w-10 h-10" style={{ color: "#e8eaf0" }} />
          <p className="text-[14px] font-semibold" style={{ color: "#94a3b8" }}>Nenhum aviso ativo</p>
          <p className="text-[12px]" style={{ color: "#c4c9d6" }}>Crie o primeiro aviso para a equipe</p>
        </div>
      ) : (
        <div className="space-y-3">
          {ativos.map((a) => (
            <AvisoCard
              key={a.id}
              aviso={a}
              lido={leituras.has(a.id)}
              nomeUsuario={nomeUsuario}
              onArquivar={() => arquivar(a)}
              onEditar={() => abrirEditar(a)}
              onLido={() => handleLido(a)}
            />
          ))}
        </div>
      )}

      {/* Seção Arquivados */}
      {arquivados.length > 0 && (
        <div className="mt-6">
          <button
            type="button"
            onClick={() => setArquivExpand((v) => !v)}
            className="w-full flex items-center justify-between px-1 mb-3"
          >
            <div className="flex items-center gap-2">
              <Archive className="w-3.5 h-3.5" style={{ color: "#94a3b8" }} />
              <p className="text-[11px] font-bold tracking-[0.1em] uppercase" style={{ color: "#94a3b8" }}>
                Arquivados ({arquivados.length})
              </p>
            </div>
            {arquivExpand
              ? <ChevronUp className="w-4 h-4" style={{ color: "#94a3b8" }} />
              : <ChevronDown className="w-4 h-4" style={{ color: "#94a3b8" }} />}
          </button>

          {arquivExpand && (
            <div className="space-y-2">
              {arquivados.slice(0, 20).map((a) => {
                const cfg = NIVEL_CFG[normNivel(a.nivel)];
                const exp = expirado(a.validade);
                return (
                  <div
                    key={a.id}
                    className="rounded-2xl px-4 py-3 flex items-start justify-between gap-3"
                    style={{ background: "#f8f9fc", border: "1px solid #e8eaf0" }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: cfg.dot, opacity: 0.4 }} />
                        <p className="text-[13px] font-semibold line-through" style={{ color: "#94a3b8" }}>{a.titulo}</p>
                      </div>
                      <p className="text-[11px]" style={{ color: "#c4c9d6" }}>
                        {exp ? `Expirou em ${fmtDate(a.validade)}` : `Arquivado por ${a.arquivado_por}`}
                        {" · "}{fmtDT(a.arquivado_em || a.criado_em)}
                      </p>
                    </div>
                    <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: exp ? "#fee2e2" : "#f1f5f9" }}>
                      {exp
                        ? <Clock className="w-3 h-3" style={{ color: "#ef4444" }} />
                        : <Check className="w-3 h-3" style={{ color: "#94a3b8" }} />}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modal criar/editar */}
      {modalAberto && (
        <div
          className="fixed inset-0 z-[999] flex items-end justify-center p-4"
          style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
          onClick={fecharModal}
        >
          <div
            className="w-full max-w-md rounded-3xl overflow-hidden"
            style={{ background: "#ffffff", border: "1px solid #e8eaf0" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid #e8eaf0" }}>
              <p className="font-bold text-[15px] text-[#0f172a]">{editando ? "Editar aviso" : "Novo aviso"}</p>
              <button type="button" onClick={fecharModal} className="w-8 h-8 flex items-center justify-center rounded-xl" style={{ background: "#f1f5f9" }}>
                <X className="w-4 h-4 text-[#64748b]" />
              </button>
            </div>

            <div className="p-5 flex flex-col gap-4">
              {/* Prioridade */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: "#64748b" }}>
                  Prioridade
                </label>
                <div className="flex gap-2">
                  {Object.entries(NIVEL_CFG).map(([key, cfg]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, nivel: key }))}
                      className="flex-1 py-2.5 rounded-xl text-[12px] font-semibold transition"
                      style={
                        form.nivel === key
                          ? { background: cfg.bg, border: `2px solid ${cfg.dot}`, color: cfg.text }
                          : { background: "#f1f5f9", border: "2px solid transparent", color: "#64748b" }
                      }
                    >
                      {cfg.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Título */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: "#64748b" }}>
                  Título *
                </label>
                <input
                  type="text"
                  value={form.titulo}
                  onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))}
                  placeholder="Ex: Portão lateral com defeito"
                  autoFocus
                  className="w-full px-4 py-3 rounded-xl text-[14px] bg-white text-[#0f172a] placeholder:text-[#94a3b8] focus:outline-none"
                  style={{ border: "1px solid #e8eaf0" }}
                />
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: "#64748b" }}>
                  Descrição (opcional)
                </label>
                <textarea
                  value={form.descricao}
                  onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
                  placeholder="Detalhes adicionais..."
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl text-[14px] bg-white text-[#0f172a] placeholder:text-[#94a3b8] focus:outline-none resize-none"
                  style={{ border: "1px solid #e8eaf0" }}
                />
              </div>

              {/* Validade */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: "#64748b" }}>
                  Válido até (opcional)
                </label>
                <input
                  type="date"
                  value={form.validade}
                  onChange={(e) => setForm((f) => ({ ...f, validade: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl text-[14px] bg-white text-[#0f172a] focus:outline-none"
                  style={{ border: "1px solid #e8eaf0" }}
                />
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={fecharModal}
                  className="flex-none px-5 py-3 rounded-2xl text-sm font-semibold"
                  style={{ background: "#f1f5f9", color: "#64748b" }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={salvar}
                  disabled={saving || !form.titulo.trim()}
                  className="flex-1 py-3 rounded-2xl text-white font-bold text-sm active:scale-[0.98] transition disabled:opacity-40"
                  style={{ background: "#08111F" }}
                >
                  {saving ? "Salvando…" : "Salvar aviso"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MobileLayout>
  );
}

function AvisoCard({ aviso, lido, nomeUsuario, onArquivar, onEditar, onLido }) {
  const nivel = normNivel(aviso.nivel);
  const cfg   = NIVEL_CFG[nivel];
  const podeEditar   = aviso.criado_por === nomeUsuario;
  const podeArquivar = aviso.criado_por === nomeUsuario;
  const edicoes      = aviso.historico_edicoes || [];

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: "#fff", border: `1px solid ${lido ? "#e8eaf0" : cfg.border}` }}
    >
      {/* Cabeçalho colorido */}
      <div
        className="px-4 py-2.5 flex items-center justify-between"
        style={{ background: lido ? "#f8f9fc" : cfg.bg, borderBottom: `1px solid ${lido ? "#e8eaf0" : cfg.border}` }}
      >
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: lido ? "#cbd5e1" : cfg.dot }} />
          <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: lido ? "#94a3b8" : cfg.text }}>
            {cfg.label}
          </span>
          {!lido && (
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500" title="Não lido" />
          )}
        </div>
        <div className="flex items-center gap-2">
          {aviso.validade && (
            <span
              className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md"
              style={{ background: "#f1f5f9", color: "#64748b" }}
            >
              Até {fmtDate(aviso.validade)}
            </span>
          )}
          <span className="flex items-center gap-1 text-[11px]" style={{ color: "#94a3b8" }}>
            <Clock className="w-3 h-3" />
            {fmtDT(aviso.criado_em)}
          </span>
        </div>
      </div>

      {/* Corpo */}
      <div className="px-4 py-3">
        <p className="font-bold text-[15px] text-[#0f172a] leading-snug">{aviso.titulo}</p>
        {aviso.descricao && (
          <p className="text-[13px] mt-1.5 leading-relaxed" style={{ color: "#475569" }}>{aviso.descricao}</p>
        )}
        <p className="text-[11px] mt-2" style={{ color: "#94a3b8" }}>
          Por {aviso.criado_por}
          {edicoes.length > 0 && <span> · editado {edicoes.length}×</span>}
          {lido && <span className="ml-2 text-emerald-600 font-semibold">✓ Lido</span>}
        </p>
      </div>

      {/* Ações */}
      <div className="px-4 pb-3 flex gap-2">
        {!lido ? (
          <button
            type="button"
            onClick={onLido}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[13px] font-bold transition active:scale-[0.97]"
            style={{ background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1d4ed8" }}
          >
            <Eye className="w-3.5 h-3.5" />
            Li o aviso
          </button>
        ) : (
          <div
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[13px] font-semibold"
            style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#16a34a" }}
          >
            <Check className="w-3.5 h-3.5" />
            Aviso lido
          </div>
        )}
        {podeEditar && (
          <button
            type="button"
            onClick={onEditar}
            className="px-3 py-2.5 rounded-xl text-[13px] font-semibold transition active:scale-[0.97]"
            style={{ background: "#f1f5f9", border: "1px solid #e8eaf0", color: "#475569" }}
          >
            Editar
          </button>
        )}
        {podeArquivar && (
          <button
            type="button"
            onClick={onArquivar}
            className="px-3 py-2.5 rounded-xl text-[13px] font-semibold transition active:scale-[0.97]"
            style={{ background: "#f1f5f9", border: "1px solid #e8eaf0", color: "#94a3b8" }}
            title="Arquivar aviso"
          >
            <Archive className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
